/**
 * Sonda real al servidor POSKN.
 * navigator.onLine miente (router encendido, sin internet / corte de luz).
 * Si una petición se cuelga, no debe dejar el POS en “offline” para siempre.
 */

const PROBE_INTERVAL_MS = 12000;
const PROBE_INTERVAL_OFFLINE_MS = 8000;
const PROBE_TIMEOUT_MS = 5000;
const PROBE_BACKOFF_MAX_MS = 60000;

function cfgOf(win) {
	return win.POSKN_API_CONFIG || {};
}

function healthUrl(win) {
	const base = String(cfgOf(win).restBase || '').replace(/\/$/, '');
	if (!base) return '';
	return `${base}/health?_ts=${Date.now()}`;
}

function applyReachable(win, ok) {
	const prev = win.posknServerReachable;
	win.posknServerReachable = !!ok;
	if (ok) {
		win._posknOfflineOrderMode = false;
	}
	if (prev === ok) {
		if (ok && typeof win.posknResumeLiveStock === 'function' && win._posknStockDegraded) {
			win.posknResumeLiveStock();
		}
		return;
	}
	if (typeof win.posknUpdateConnectionStatus === 'function') {
		win.posknUpdateConnectionStatus();
	}
	if (ok && prev === false) {
		if (typeof win.mostrarNotificacion === 'function') {
			win.mostrarNotificacion('Conexión recuperada. Subiendo pendientes si hay.', 'success');
		}
		if (typeof win.posknResumeLiveStock === 'function') {
			win.posknResumeLiveStock();
		}
		if (
			typeof win.getColaPendientesSubir === 'function' &&
			win.getColaPendientesSubir().length > 0 &&
			typeof win.procesarColaSubida === 'function'
		) {
			win.procesarColaSubida();
		}
	} else if (!ok && prev !== false) {
		if (typeof win.mostrarNotificacion === 'function') {
			win.mostrarNotificacion(
				'Sin respuesta del servidor. Sigue vendiendo: los cobros van a cola local.',
				'warning',
			);
		}
	}
}

function fetchHealth(win, externalController) {
	const url = healthUrl(win);
	if (!url) {
		return Promise.resolve(null);
	}
	const controller =
		externalController || (typeof AbortController !== 'undefined' ? new AbortController() : null);
	const timer = win.setTimeout(() => {
		if (controller) {
			try {
				controller.abort();
			} catch (e) {
				/* ignore */
			}
		}
	}, PROBE_TIMEOUT_MS);
	const opts = {
		method: 'GET',
		credentials: 'same-origin',
		cache: 'no-store',
		headers: {
			Accept: 'application/json',
			'Cache-Control': 'no-cache',
		},
	};
	if (controller) {
		opts.signal = controller.signal;
	}
	return win
		.fetch(url, opts)
		.then((res) => {
			win.clearTimeout(timer);
			if (res.ok) return true;
			if (res.status === 401 || res.status === 403) return true;
			return false;
		})
		.catch(() => {
			win.clearTimeout(timer);
			return false;
		});
}

function keepaliveFallback(win, $) {
	const cfg = cfgOf(win);
	const url = cfg.ajaxUrl || 'pos-ajax.php';
	const nonce = win.posNonce || cfg.posNonce || '';
	return new Promise((resolve) => {
		$.ajax({
			url,
			method: 'POST',
			data: { action: 'pos_keepalive', nonce, _t: Date.now() },
			dataType: 'json',
			timeout: PROBE_TIMEOUT_MS,
			success() {
				resolve(true);
			},
			error(xhr) {
				if (xhr && (xhr.status === 401 || xhr.status === 403)) {
					resolve(true);
					return;
				}
				resolve(false);
			},
		});
	});
}

export function initServerProbe(win, $) {
	if (win._posknServerProbeBound) return;
	win._posknServerProbeBound = true;

	let fails = 0;
	let probing = false;
	let probeGen = 0;
	let retryAfter = false;
	let lastOkAt = 0;
	let backoffUntil = 0;
	let backoffMs = PROBE_INTERVAL_OFFLINE_MS;
	let activeController = null;

	win.posknApplyServerReachable = function posknApplyServerReachable(ok) {
		if (ok) {
			fails = 0;
			backoffMs = PROBE_INTERVAL_OFFLINE_MS;
			backoffUntil = 0;
			lastOkAt = Date.now();
		}
		applyReachable(win, ok);
	};

	win.posknIsOfflineMode = function posknIsOfflineMode() {
		if (win.posknServerReachable === true) return false;
		if (win.navigator.onLine === false) return true;
		if (win.posknServerReachable === false) return true;
		return !!win._posknOfflineOrderMode;
	};

	win.posknProbeServer = function posknProbeServer(force) {
		if (!force && backoffUntil && Date.now() < backoffUntil) {
			return;
		}
		if (probing && !force) {
			retryAfter = true;
			return;
		}
		if (activeController) {
			try {
				activeController.abort();
			} catch (e) {
				/* ignore */
			}
			activeController = null;
		}
		probing = true;
		const gen = ++probeGen;
		retryAfter = false;
		const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
		activeController = controller;

		let settled = false;
		const done = (ok) => {
			if (gen !== probeGen || settled) return;
			settled = true;
			win.clearTimeout(watchdog);
			if (activeController === controller) activeController = null;
			probing = false;
			if (ok === null) {
				if (retryAfter) {
					retryAfter = false;
					win.posknProbeServer();
				}
				return;
			}
			if (ok) {
				fails = 0;
				lastOkAt = Date.now();
				backoffMs = PROBE_INTERVAL_OFFLINE_MS;
				backoffUntil = 0;
				applyReachable(win, true);
			} else {
				fails += 1;
				backoffMs = Math.min(PROBE_BACKOFF_MAX_MS, backoffMs * 2);
				backoffUntil = Date.now() + backoffMs;
				if (fails >= 2) {
					applyReachable(win, false);
				}
			}
			if (retryAfter) {
				retryAfter = false;
				win.posknProbeServer();
			}
		};

		const watchdog = win.setTimeout(() => {
			try {
				if (controller) controller.abort();
			} catch (e) {
				/* ignore */
			}
			done(false);
		}, PROBE_TIMEOUT_MS + 1500);

		fetchHealth(win, controller)
			.then((ok) => {
				if (ok === null) {
					return keepaliveFallback(win, $);
				}
				return ok;
			})
			.then(done)
			.catch(() => done(false));
	};

	$(() => {
		win.setTimeout(() => win.posknProbeServer(), 800);
		win.setInterval(() => {
			const markedOffline =
				win.posknServerReachable === false || win.navigator.onLine === false;
			const wait = markedOffline ? backoffMs : PROBE_INTERVAL_MS;
			if (!markedOffline && lastOkAt && Date.now() - lastOkAt < wait) {
				return;
			}
			if (markedOffline && backoffUntil && Date.now() < backoffUntil) {
				return;
			}
			win.posknProbeServer();
		}, PROBE_INTERVAL_OFFLINE_MS);
	});

	const reconnectKick = () => {
		fails = 0;
		backoffMs = PROBE_INTERVAL_OFFLINE_MS;
		backoffUntil = 0;
		win.setTimeout(() => win.posknProbeServer(true), 400);
		win.setTimeout(() => win.posknProbeServer(true), 1600);
		win.setTimeout(() => win.posknProbeServer(true), 4000);
	};

	win.addEventListener('online', reconnectKick);
	win.addEventListener('offline', () => {
		fails = 2;
		applyReachable(win, false);
	});

	document.addEventListener('visibilitychange', () => {
		if (document.visibilityState === 'visible') {
			win.posknProbeServer(true);
		}
	});
}
